import { Component } from '@theme/component';

/**
 * Floating "Size & Room Preview" widget.
 * Appears when the buy buttons scroll out of view,
 * opens the size guide visualiser dialog on click.
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsObserver = null;

  /** @type {IntersectionObserver | null} */
  #footerObserver = null;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {boolean} */
  #hiddenByFooter = false;

  connectedCallback() {
    super.connectedCallback();
    this.#setupObservers();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsObserver?.disconnect();
    this.#footerObserver?.disconnect();
  }

  #setupObservers() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    const footer =
      document.querySelector('footer') ??
      document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Hide if no size guide dialog exists on the page
    if (!this.#getDialog()) return;

    this.#buyButtonsObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting && !this.#isStuck) {
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          this.#show();
        }
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByFooter = false;
        this.#hide();
      }
    });

    this.#footerObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByFooter = true;
          this.#hide();
        } else if (!entry.isIntersecting && this.#hiddenByFooter) {
          const rect = buyButtonsBlock.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByFooter = false;
            this.#show();
          }
        }
      },
      { rootMargin: '200px 0px 0px 0px' }
    );

    this.#buyButtonsObserver.observe(buyButtonsBlock);
    this.#footerObserver.observe(footer);
  }

  /**
   * Opens the size guide visualiser dialog.
   */
  handleOpenVisualiser = () => {
    const dialog = this.#getDialog();
    if (dialog && typeof dialog.showDialog === 'function') {
      dialog.showDialog();
    }
  };

  #show() {
    this.refs.stickyBar.dataset.stuck = 'true';
    this.#isStuck = true;
  }

  #hide() {
    this.refs.stickyBar.dataset.stuck = 'false';
    this.#isStuck = false;
  }

  /**
   * Finds the size guide dialog component on the page.
   * @returns {HTMLElement | null}
   */
  #getDialog() {
    return document.querySelector('dialog-component.size-guide-modal');
  }

  /**
   * Gets the product form element.
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
